import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ErrorState } from '@ui-kit';
import { simulatorFormSchema, type SimulatorFormValues } from '../schema/permissionDemoSchema';
import { useSimulate } from '../hooks/useSimulate';

const SYSTEM_ROLES = [
  { code: 'ROLE_ADMIN', hint: '모든 권한' },
  { code: 'ROLE_MANAGER', hint: 'USER_READ + AUDIT_READ' },
  { code: 'ROLE_USER', hint: '기본 권한' },
  { code: 'ROLE_GUEST', hint: '권한 없음' },
] as const;

const permChipStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '3px var(--spacing-3)',
  borderRadius: 'var(--radius-full)',
  fontFamily: 'var(--font-family-mono)',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 600,
  border: '1px solid var(--color-success)',
  color: 'var(--color-success)',
  background: 'color-mix(in srgb, var(--color-success) 12%, transparent)',
};

export function RoleSimulatorTab() {
  const [submittedRole, setSubmittedRole] = useState<string | null>(null);

  const { control, handleSubmit, watch, formState: { errors } } = useForm<SimulatorFormValues>({
    resolver: zodResolver(simulatorFormSchema),
    defaultValues: { selectedRole: 'ROLE_USER' },
  });

  const currentRole = watch('selectedRole');

  const { data, isLoading, isError, error, refetch } = useSimulate(submittedRole);

  const onSubmit = (values: SimulatorFormValues) => {
    setSubmittedRole(values.selectedRole);
  };

  return (
    <div>
      <div
        style={{
          background: 'var(--color-surface-muted)',
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          padding: 'var(--spacing-4)',
          marginBottom: 'var(--spacing-4)',
          display: 'grid',
          gridTemplateColumns: 'minmax(280px, 320px) 1fr',
          gap: 'var(--spacing-4)',
          alignItems: 'start',
        }}
        // On mobile (< 768px) fallback handled via minmax
      >
        {/* Left: role selector form */}
        <form
          onSubmit={handleSubmit(onSubmit)}
          aria-label="역할 시뮬레이터 폼"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--spacing-3)',
            padding: 'var(--spacing-3)',
            background: 'var(--color-surface-elevated)',
            border: '1px solid var(--color-surface-border)',
            borderRadius: 'var(--radius-md)',
          }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: 'var(--font-size-sm)',
              fontWeight: 700,
              color: 'var(--color-text-primary)',
            }}
          >
            역할 선택
          </h3>

          <fieldset
            style={{ border: 'none', margin: 0, padding: 0 }}
            aria-describedby={errors.selectedRole ? 'role-error' : undefined}
          >
            <legend
              style={{
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-secondary)',
                fontWeight: 600,
                marginBottom: 'var(--spacing-2)',
                display: 'block',
              }}
            >
              시뮬레이션할 역할
            </legend>

            <Controller
              name="selectedRole"
              control={control}
              render={({ field }) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-2)' }}>
                  {SYSTEM_ROLES.map((role) => {
                    const checked = field.value === role.code;
                    return (
                      <label
                        key={role.code}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--spacing-2)',
                          padding: 'var(--spacing-2)',
                          border: `1px solid ${checked ? 'var(--color-brand-primary)' : 'var(--color-surface-border)'}`,
                          borderRadius: 'var(--radius-sm)',
                          background: checked
                            ? 'color-mix(in srgb, var(--color-brand-primary) 10%, transparent)'
                            : 'var(--color-surface-background)',
                          cursor: 'pointer',
                          fontSize: 'var(--font-size-sm)',
                        }}
                      >
                        <input
                          type="radio"
                          {...field}
                          value={role.code}
                          checked={checked}
                          onChange={() => field.onChange(role.code)}
                          style={{ accentColor: 'var(--color-brand-primary)', flexShrink: 0 }}
                          aria-label={`역할 선택: ${role.code}`}
                        />
                        <span
                          style={{
                            fontFamily: 'var(--font-family-mono)',
                            fontSize: 'var(--font-size-xs)',
                            fontWeight: 700,
                          }}
                        >
                          {role.code}
                        </span>
                        <span
                          style={{
                            color: 'var(--color-text-muted)',
                            fontSize: 'var(--font-size-xs)',
                            marginLeft: 'auto',
                          }}
                        >
                          {role.hint}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
            />

            {errors.selectedRole && (
              <p
                id="role-error"
                role="alert"
                aria-live="polite"
                style={{
                  color: 'var(--color-danger)',
                  fontSize: 'var(--font-size-xs)',
                  marginTop: 'var(--spacing-2)',
                }}
              >
                {errors.selectedRole.message}
              </p>
            )}
          </fieldset>

          <button
            type="submit"
            disabled={isLoading}
            aria-busy={isLoading}
            style={{
              background: 'var(--color-brand-primary)',
              color: 'var(--color-text-inverse)',
              border: 'none',
              padding: 'var(--spacing-2) var(--spacing-4)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.6 : 1,
              fontFamily: 'var(--font-family-sans)',
            }}
          >
            {isLoading ? '시뮬레이션 중...' : '시뮬레이트'}
          </button>
        </form>

        {/* Right: results */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-3)' }}>
          {!submittedRole && (
            <div
              style={{
                padding: 'var(--spacing-8)',
                textAlign: 'center',
                color: 'var(--color-text-muted)',
                fontSize: 'var(--font-size-sm)',
                fontStyle: 'italic',
                border: '1px dashed var(--color-surface-border)',
                borderRadius: 'var(--radius-md)',
              }}
            >
              왼쪽에서 역할을 선택하고 [시뮬레이트] 를 클릭하세요
            </div>
          )}

          {isLoading && submittedRole && (
            <div
              aria-busy="true"
              aria-label="시뮬레이션 중"
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--spacing-3)',
              }}
            >
              {[80, 60, 100].map((w, i) => (
                <div
                  key={i}
                  aria-hidden="true"
                  style={{
                    height: 40,
                    width: `${w}%`,
                    borderRadius: 'var(--radius-sm)',
                    background: 'color-mix(in srgb, var(--color-text-muted) 20%, transparent)',
                    animation: 'pulse 1.5s ease-in-out infinite',
                  }}
                />
              ))}
            </div>
          )}

          {isError && (
            <ErrorState
              title="시뮬레이션 오류"
              message={
                error?.code === 'PERM-DEMO-400-001'
                  ? `존재하지 않는 역할 코드입니다: ${submittedRole} — 매트릭스 탭의 역할 목록을 참조하세요.`
                  : (error?.message ?? '시뮬레이션 중 오류가 발생했습니다.')
              }
              onRetry={() => refetch()}
            />
          )}

          {data && !isLoading && (
            <>
              {/* Trust boundary notice */}
              <div
                style={{
                  padding: 'var(--spacing-2) var(--spacing-3)',
                  background: 'color-mix(in srgb, var(--color-warning) 10%, transparent)',
                  border: '1px solid var(--color-warning)',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-secondary)',
                }}
                role="note"
              >
                이 결과는 서버 예측입니다. 실제 접근 결과는 "데모 endpoint" 탭에서 확인하세요.
              </div>

              {/* Assumed roles */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '120px 1fr',
                  gap: 'var(--spacing-3)',
                  padding: 'var(--spacing-2) var(--spacing-3)',
                  border: '1px solid var(--color-surface-border)',
                  borderRadius: 'var(--radius-sm)',
                  background: 'var(--color-surface-elevated)',
                  fontSize: 'var(--font-size-sm)',
                  alignItems: 'center',
                }}
              >
                <span
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-secondary)',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  가정 역할
                </span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-2)' }}>
                  {data.assumedRoles.map((r) => (
                    <span
                      key={r}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '3px var(--spacing-3)',
                        borderRadius: 'var(--radius-full)',
                        fontFamily: 'var(--font-family-mono)',
                        fontSize: 'var(--font-size-xs)',
                        fontWeight: 600,
                        border: '1px solid var(--color-info)',
                        color: 'var(--color-info)',
                        background: 'color-mix(in srgb, var(--color-info) 12%, transparent)',
                      }}
                    >
                      {r}
                    </span>
                  ))}
                </div>
              </div>

              {/* Computed permissions */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '120px 1fr',
                  gap: 'var(--spacing-3)',
                  padding: 'var(--spacing-2) var(--spacing-3)',
                  border: '1px solid var(--color-surface-border)',
                  borderRadius: 'var(--radius-sm)',
                  background: 'var(--color-surface-elevated)',
                  fontSize: 'var(--font-size-sm)',
                  alignItems: 'flex-start',
                }}
              >
                <span
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-secondary)',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    paddingTop: 4,
                  }}
                >
                  계산된 권한
                </span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-2)' }}>
                  {data.computedPermissions.length === 0 ? (
                    <span style={{ color: 'var(--color-text-muted)', fontStyle: 'italic', fontSize: 'var(--font-size-sm)' }}>
                      이 역할에 부여된 권한 없음
                    </span>
                  ) : (
                    data.computedPermissions.map((p) => (
                      <span key={p} style={permChipStyle}>{p}</span>
                    ))
                  )}
                </div>
              </div>

              {/* Endpoint predictions table */}
              <div>
                <p
                  style={{
                    margin: '0 0 var(--spacing-2)',
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-secondary)',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  endpoint 예측
                </p>
                <table
                  aria-label="endpoint 예측 결과"
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: 'var(--font-size-sm)',
                    background: 'var(--color-surface-elevated)',
                    border: '1px solid var(--color-surface-border)',
                    borderRadius: 'var(--radius-sm)',
                    overflow: 'hidden',
                  }}
                >
                  <thead>
                    <tr>
                      {['경로', '예상 상태', '이유'].map((h) => (
                        <th
                          key={h}
                          scope="col"
                          style={{
                            padding: 'var(--spacing-2) var(--spacing-3)',
                            borderBottom: '1px solid var(--color-surface-border)',
                            textAlign: 'left',
                            fontSize: 'var(--font-size-xs)',
                            color: 'var(--color-text-secondary)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            fontWeight: 700,
                            background: 'var(--color-surface-muted)',
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.endpointPredictions.map((pred, idx) => (
                      <tr key={pred.path}>
                        <td
                          style={{
                            padding: 'var(--spacing-2) var(--spacing-3)',
                            borderBottom: idx < data.endpointPredictions.length - 1 ? '1px solid var(--color-surface-border)' : 'none',
                            fontFamily: 'var(--font-family-mono)',
                            fontSize: 'var(--font-size-xs)',
                            color: 'var(--color-text-primary)',
                          }}
                        >
                          {pred.path}
                        </td>
                        <td
                          style={{
                            padding: 'var(--spacing-2) var(--spacing-3)',
                            borderBottom: idx < data.endpointPredictions.length - 1 ? '1px solid var(--color-surface-border)' : 'none',
                          }}
                        >
                          <span
                            style={{
                              display: 'inline-block',
                              fontFamily: 'var(--font-family-mono)',
                              fontSize: 'var(--font-size-xs)',
                              fontWeight: 700,
                              padding: '2px var(--spacing-2)',
                              borderRadius: 'var(--radius-full)',
                              background: pred.expectedStatus === 200 ? 'var(--color-success)' : 'var(--color-surface-muted)',
                              color: pred.expectedStatus === 200 ? 'var(--color-text-inverse)' : 'var(--color-text-secondary)',
                              border: pred.expectedStatus === 403 ? '1px solid var(--color-text-muted)' : 'none',
                            }}
                            aria-label={pred.expectedStatus === 200 ? '통과 예상' : '거부 예상'}
                          >
                            {pred.expectedStatus}
                          </span>
                        </td>
                        <td
                          style={{
                            padding: 'var(--spacing-2) var(--spacing-3)',
                            borderBottom: idx < data.endpointPredictions.length - 1 ? '1px solid var(--color-surface-border)' : 'none',
                            color: 'var(--color-text-muted)',
                            fontSize: 'var(--font-size-xs)',
                          }}
                        >
                          {pred.reason}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Hint footer */}
      <p
        style={{
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-text-muted)',
          margin: 0,
        }}
      >
        현재 선택: <code style={{ fontFamily: 'var(--font-family-mono)', color: 'var(--color-brand-primary)' }}>{currentRole}</code>
        {' — 매트릭스 탭에서 역할별 권한 목록을 확인할 수 있습니다.'}
      </p>
    </div>
  );
}
